import { Button, Input } from "@l30/ui";
import { useEffect, useId, useRef, useState } from "react";

import { isLockedAskSource } from "./locked-ask-source.js";
import { sourceChipLabel } from "./source-labels.js";

import type { AiAttachmentRef, AiSourceRef, AiSourceSearchResponse } from "@l30/shared";

type SourceChip = AiSourceRef & { title?: string; subtitle?: string };

interface MapRow {
  id: string;
  title: string;
}

interface AiSourcesStripProps {
  sources: SourceChip[];
  attachments: AiAttachmentRef[];
  pickerQ: string;
  onPickerQChange: (q: string) => void;
  pickerHits: AiSourceSearchResponse["hits"];
  maps: MapRow[];
  mapsLoading: boolean;
  mapsError: boolean;
  ragFlag: boolean;
  ragEnabled: boolean;
  onRagEnabledChange: (v: boolean) => void;
  personalMode: boolean;
  onPersonalModeChange: (v: boolean) => void;
  onAddSource: (hit: AiSourceSearchResponse["hits"][number]) => void;
  onRemoveSource: (type: string, id: string) => void;
  onRemoveAttachment: (key: string) => void;
  onAddKnowledgeMap: (map: MapRow) => void;
  onNewChatInMap?: (map: MapRow) => void;
  onOpenInOffice?: (documentId: string) => void;
  /** Primary pin that cannot be removed (Subject Ask panel). */
  lockedSource?: { type: string; id: string };
  /** Hide Workspace RAG when a Subject pin already suppresses overflow. */
  hideRag?: boolean;
}

/* eslint-disable-next-line max-lines-per-function -- picker, maps, and pin chips share one footer */
export function AiSourcesStrip({
  sources,
  attachments,
  pickerQ,
  onPickerQChange,
  pickerHits,
  maps,
  mapsLoading,
  mapsError,
  ragFlag,
  ragEnabled,
  onRagEnabledChange,
  personalMode,
  onPersonalModeChange,
  onAddSource,
  onRemoveSource,
  onRemoveAttachment,
  onAddKnowledgeMap,
  onNewChatInMap,
  onOpenInOffice,
  lockedSource,
  hideRag = false,
}: AiSourcesStripProps) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const count = sources.length + attachments.length;

  return (
    <div ref={rootRef} className="relative space-y-2 border-t border-border pt-2">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          className="rounded-soft-md border border-border bg-surface px-2.5 py-1 text-xs font-medium text-fg hover:bg-muted"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => setOpen((v) => !v)}
        >
          {open
            ? "Close sources"
            : count > 0
              ? `${count} source${count === 1 ? "" : "s"} · Add`
              : "Add source"}
        </button>
        {sources.map((s) => {
          const locked = isLockedAskSource(s, lockedSource);
          const extra =
            lockedSource && !locked ? (
              <span className="text-[10px] uppercase tracking-wide text-muted-fg">
                Additional context
              </span>
            ) : null;
          return (
            <span
              key={`${s.type}:${s.id}`}
              className="inline-flex max-w-full flex-wrap items-center gap-1 rounded-soft-md border border-border px-2 py-0.5 text-xs"
            >
              {locked ? (
                <span className="text-[10px] uppercase tracking-wide text-muted-fg">
                  Asking about
                </span>
              ) : (
                extra
              )}
              {s.type === "working_document" ? (
                <span className="text-[10px] uppercase tracking-wide text-muted-fg">Office</span>
              ) : null}
              <span className="min-w-0 truncate">
                {locked && s.title ? s.title : sourceChipLabel(s)}
              </span>
              {s.type === "working_document" && onOpenInOffice ? (
                <button
                  type="button"
                  className="text-muted-fg underline hover:text-fg"
                  onClick={() => onOpenInOffice(s.id)}
                >
                  Open
                </button>
              ) : null}
              {locked ? null : (
                <button
                  type="button"
                  aria-label="Remove source"
                  className="text-muted-fg hover:text-fg"
                  onClick={() => onRemoveSource(s.type, s.id)}
                >
                  ×
                </button>
              )}
            </span>
          );
        })}
        {attachments.map((a) => (
          <span
            key={a.key}
            className="inline-flex items-center gap-1 rounded-soft-md border border-border bg-surface-muted px-2 py-0.5 text-xs"
          >
            {a.filename}
            <button
              type="button"
              aria-label="Remove attachment"
              className="text-muted-fg hover:text-fg"
              onClick={() => onRemoveAttachment(a.key)}
            >
              ×
            </button>
          </span>
        ))}
      </div>

      {open ? (
        <div
          id={panelId}
          className="absolute bottom-full left-0 z-20 mb-2 w-full max-w-lg space-y-2 rounded-soft-md border border-border bg-surface p-3 shadow-sm"
        >
          <Input
            value={pickerQ}
            onChange={(e) => onPickerQChange(e.target.value)}
            placeholder="Search notes, docs, working docs, CRM, Knowledge…"
            className="w-full"
          />
          {pickerHits.length > 0 ? (
            <ul className="max-h-40 overflow-y-auto rounded-soft-md border border-border text-sm">
              {pickerHits.map((h) => (
                <li key={`${h.type}:${h.id}`}>
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 px-2 py-1.5 text-left hover:bg-muted"
                    onClick={() => {
                      onAddSource(h);
                      setOpen(false);
                      onPickerQChange("");
                    }}
                  >
                    <span className="text-[10px] uppercase text-muted-fg">
                      {h.type === "ideation_map" ? "map" : h.type === "project" ? "crm" : h.type}
                    </span>
                    <span className="min-w-0 flex-1 truncate">
                      {h.title}
                      {h.subtitle ? (
                        <span className="ml-1 text-muted-fg">· {h.subtitle}</span>
                      ) : null}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ) : null}

          <div className="space-y-1.5 border-t border-border pt-2">
            <p className="text-xs font-medium text-muted-fg">
              Knowledge projects (maps) — not CRM projects
            </p>
            {mapsLoading ? (
              <p className="text-xs text-muted-fg">Loading…</p>
            ) : mapsError ? (
              <p className="text-xs text-muted-fg">
                Could not list maps. Search above still finds Knowledge projects.
              </p>
            ) : maps.length === 0 ? (
              <p className="text-xs text-muted-fg">No maps yet. Create one under Ideation.</p>
            ) : (
              <ul className="flex max-h-28 flex-col gap-1 overflow-y-auto">
                {maps.slice(0, 12).map((m) => (
                  <li key={m.id} className="flex flex-wrap items-center gap-1.5">
                    <span className="min-w-0 flex-1 truncate text-sm text-fg">{m.title}</span>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={() => {
                        onAddKnowledgeMap(m);
                        setOpen(false);
                      }}
                    >
                      Add as source
                    </Button>
                    {onNewChatInMap ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          void onNewChatInMap(m);
                          setOpen(false);
                        }}
                      >
                        New chat in map
                      </Button>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex flex-wrap gap-3 border-t border-border pt-2 text-xs text-muted-fg">
            {ragFlag && !hideRag ? (
              <label className="flex items-center gap-1.5">
                <input
                  type="checkbox"
                  checked={ragEnabled}
                  onChange={(e) => onRagEnabledChange(e.target.checked)}
                />
                Workspace RAG
              </label>
            ) : null}
            <label className="flex items-center gap-1.5">
              <input
                type="checkbox"
                checked={personalMode}
                onChange={(e) => onPersonalModeChange(e.target.checked)}
              />
              Personal
            </label>
          </div>
        </div>
      ) : null}
    </div>
  );
}
